/**
 * Pure per-render filter for the dashboard nav config. The config module is a
 * shared singleton, so this must never mutate it (a destructive filter here
 * permanently deleted entries — e.g. the Nimi invoices link stayed hidden after
 * enabling the provider until a full page reload).
 *
 * ctx: { user, marketplace, primaryAccount, userAllowToAccess,
 *        isLimitedRole, isCashier, routeParams }
 */
export function filterNavConfig(navConfig, ctx) {
    const { user, marketplace, primaryAccount, userAllowToAccess, isLimitedRole, isCashier, routeParams = {} } = ctx;

    const shouldRenderItem = (item) => {
        if (item.isSellerRoute) {
            if (isLimitedRole) return false;
            return user?.isSeller;
        }
        if (item.isCashierRoute) {
            return primaryAccount?.hasCashier && userAllowToAccess(item.minimumRoleAllowedAccess);
        }
        // #1309 — cashier is authorised for the whole subscription surface on the backend
        // (create/manage editor-tier, delete admin-tier), but the seller item that leads there
        // (Customer Club) is nested under the "System Management" parent, which is itself an
        // `isSellerRoute` item cashier can't see — so nothing under it is ever reached for them.
        // This is a dedicated top-level entry to the same page, visible to cashier only.
        if (item.isCashierSeasonTicketRoute) {
            return isCashier && user?.isSeller && marketplace?.isAllowSeasonTicket && marketplace?.hasCRM;
        }
        // #1309 — same reasoning as isCashierSeasonTicketRoute above: My Customers is nested
        // under the same unreachable-for-cashier "System Management" parent, but its own page
        // (a specific customer's Customer Club tab) already lets a cashier create/manage a
        // subscription with no plan-authoring UI at all — a dedicated entry for discoverability.
        if (item.isCashierEndUsersRoute) {
            return isCashier && user?.isSeller && marketplace?.hasCRM;
        }
        if (item.isCalendarRoute) {
            if (isLimitedRole) return false;
            return marketplace?.hasCalendar;
        }
        if (item.isSellersManagerRoute) {
            return marketplace?.isSellersManager;
        }
        if (item.isCrmRoute) {
            return marketplace?.hasCRM;
        }
        if (item.isMarketPlaceOwnerRoute) {
            return user?.isMarketPlaceOwner;
        }
        if (item.isAllowSeasonTicketRoute) {
            return marketplace?.isAllowSeasonTicket;
        }
        if (item.isPromoterRoute) {
            return user?.isPromoter;
        }
        if (item.minimumRoleAllowedAccess) {
            return userAllowToAccess(item.minimumRoleAllowedAccess);
        }
        return true;
    };

    const shouldRenderChild = (childRoute) => {
        if (childRoute.isAllowSeasonTicketRoute && !marketplace?.isAllowSeasonTicket) return false;
        if (childRoute.isCrmRoute && !marketplace?.hasCRM) return false;
        if (childRoute.isSellerRoute && !user?.isSeller) return false;
        if (childRoute.isGroupOrdersRoute && !primaryAccount?.hasGroupOrders) return false;
        if (childRoute.isSellersManagerRoute && !marketplace?.isSellersManager) return false;
        if (childRoute.isMarketPlaceOwnerRoute && !user?.isMarketPlaceOwner) return false;
        return true;
    };

    return navConfig
        .filter((navItem) => !navItem.dependOn || Object.keys(routeParams).includes(navItem.dependOn))
        .map((navItem) => {
            if (!navItem.items?.length) return navItem;
            const items = navItem.items
                .filter(shouldRenderItem)
                .map((item) =>
                    item.children?.length
                        ? { ...item, children: item.children.filter(shouldRenderChild) }
                        : item
                );
            return { ...navItem, items };
        });
}
